import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

import { ENV } from '@utils/env';

import { TrackingEvent } from '@/services/trackingEvents';
import { Team } from '@/types/teams';
import { User } from '@/types/users';

const EXTENSION_SCHEMES = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'ms-browser-extension://',
  'edge-extension://',
];

function getUrl(urlOrPath: string) {
  if (urlOrPath.startsWith('http')) {
    return urlOrPath;
  }

  const url = new URL(urlOrPath, window.location.origin);
  return url.toString();
}

class MonitoringService {
  get isDebug() {
    return ENV.datadog.debugMode;
  }

  private debugLog(type: string, ...args: any[]) {
    if (this.isDebug) {
      // eslint-disable-next-line no-console
      console.log('[MONITORING]', `[${type}]`, ...args);
    }
  }

  /**
   * Checks if the given URL is for an Evervault API resource.
   */
  private isApiResource(url: string) {
    const apiResourceUrls = [
      getUrl(ENV.apiUrls.public),
      getUrl(ENV.apiUrls.internal),
      getUrl(ENV.apiUrls.internalDirect),
    ];
    return apiResourceUrls.some((resourceUrl) => url.startsWith(resourceUrl));
  }

  /**
   * Detects errors thrown from browser extensions, which are noise we have no
   * way to act on. Datadog only exposes a flat stack string, so we look for an
   * extension scheme anywhere in it.
   */
  private isExtensionEvent(event: RumEvent) {
    if (event.type !== 'error') {
      return false;
    }

    const stack = event.error.stack;

    if (!stack) {
      return false;
    }

    return EXTENSION_SCHEMES.some((scheme) => stack.includes(scheme));
  }

  /**
   * Initializes the Datadog monitoring service.
   */
  init() {
    if (!ENV.datadog.applicationId) {
      this.debugLog('init', 'Missing application ID');
      return;
    }

    if (!ENV.datadog.clientToken) {
      this.debugLog('init', 'Missing client token');
      return;
    }

    datadogRum.init({
      applicationId: ENV.datadog.applicationId,
      clientToken: ENV.datadog.clientToken,
      site: ENV.datadog.site,
      env: ENV.datadog.env,
      service: 'dashboard',
      version: ENV.datadog.version,
      trackAnonymousUser: false,

      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: 'mask',

      trackResources: true,
      trackUserInteractions: true,
      trackLongTasks: true,

      allowedTracingUrls: [(url) => this.isApiResource(url)],

      beforeSend: (event) => {
        if (this.isExtensionEvent(event)) {
          return false;
        }

        this.debugLog(`event(${event.type})`, event);
        return !this.isDebug;
      },

      plugins: [reactPlugin({ router: true })],
    });

    datadogLogs.init({
      clientToken: ENV.datadog.clientToken,
      site: ENV.datadog.site,
      env: ENV.datadog.env,
      service: 'dashboard',
      version: ENV.datadog.version,

      sessionSampleRate: 100,

      // RUM already collects console/uncaught errors, so don't duplicate them
      // as logs — this SDK is only used for explicit `logInfo` calls.
      forwardErrorsToLogs: false,

      beforeSend: (log) => {
        this.debugLog('log', log);
        return !this.isDebug;
      },
    });

    this.debugLog('init', 'SDK initialized');
  }

  /**
   * Identifies the current user.
   */
  identify(user: User) {
    datadogRum.setUser({
      id: String(user.id),
      name: user.name,
      email: user.email,
    });
    this.debugLog('identify', user);
  }

  setTeam(team?: Pick<Team, 'uuid' | 'name'>) {
    if (team) {
      datadogRum.setGlobalContextProperty('team', {
        uuid: team.uuid,
        name: team.name,
      });
    } else {
      datadogRum.removeGlobalContextProperty('team');
    }
    this.debugLog('setTeam', team);
  }

  /**
   * Reports an unexpected error to Datadog, with optional extra attributes.
   */
  reportError(error: unknown, context?: Record<string, unknown>) {
    datadogRum.addError(error, context);
    this.debugLog('error', error, context);
  }

  /**
   * Logs an informational message. Use this for noteworthy-but-expected
   * events; use `reportError` for actual application errors.
   */
  logInfo(message: string, context?: Record<string, unknown>) {
    datadogLogs.logger.info(message, context);
    this.debugLog('info', message, context);
  }

  /**
   * Tracks a custom user interaction or product-usage event. See
   * `trackingEvents.ts` for the full event registry.
   */
  trackAction<Name extends TrackingEvent['name']>(
    name: Name,
    context: Extract<TrackingEvent, { name: Name }>['context']
  ) {
    datadogRum.addAction(name, context);
    this.debugLog('action', name, context);
  }

  public observeFeatureFlagEvaluation(
    featureFlagKey: string,
    configuration: unknown
  ): void {
    datadogRum.addFeatureFlagEvaluation(featureFlagKey, configuration);
  }
}

export const monitoring = new MonitoringService();
